import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import DatePickerButton from './DatePickerButton';
import {
  initializeUrbanrideApi,
  searchLocations,
  searchAirlines,
  validateFlight,
  getTripDetails,
  TITLES
} from '../../services/urbanrideService';

/**
 * EditChauffeuredServiceModal - Edit modal for chauffeur service components
 *
 * This component provides an edit interface that matches the design of TripChauffeuredServiceBooking
 * but is tailored for editing existing chauffeur service components.
 */
const EditChauffeuredServiceModal = ({
  tripId,
  component,
  tripExecutives = [],
  allExecutives = [],
  allContacts = [],
  onSuccess,
  onClose
}) => {
  // API Configuration state
  const [apiConfigured, setApiConfigured] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);

  // Extract component data for easier access
  const componentData = component?.component_data || {};

  // Helper function to format full address display (matches TimelineComponentCard logic)
  const formatFullAddress = (type, name, address, airportCode) => {
    // Airport: show airport name/location (airline & flight number are separate fields)
    if (type === 'airport') {
      // Prefer the full airport name, fallback to code
      return name || (airportCode ? `${airportCode}` : '') || address || '';
    }
    // Hotel: combine name and address
    if (type === 'hotel') {
      if (address && name && address !== name) {
        return `${name}, ${address}`;
      }
      return address || name || 'Hotel';
    }
    // Address/Meeting: just show address (not combined with meeting title)
    return address || name || '';
  };

  // Helper for stops
  const formatStopAddress = (stop) => {
    const type = stop.type || 'address';
    const name = stop.location || '';
    const address = stop.location_address || '';

    // Hotel: combine name and address
    if (type === 'hotel') {
      if (address && name && address !== name) {
        return `${name}, ${address}`;
      }
      return address || name || 'Hotel';
    }
    // Address/Meeting: just show address (not combined with meeting title)
    return address || name || '';
  };

  // Determine initial pickup/dropoff types from component data
  const getInitialPickupType = () => {
    if (componentData.pickup_type) return componentData.pickup_type;
    if (componentData.service_type === 'airport_pickup') return 'airport';
    return 'address';
  };

  const getInitialDropoffType = () => {
    if (componentData.dropoff_type) return componentData.dropoff_type;
    if (componentData.service_type === 'airport_dropoff') return 'airport';
    return 'address';
  };

  // Parse initial datetime
  const parseDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return { date: '', time: '' };
    const parts = dateTimeStr.split('T');
    return {
      date: parts[0] || '',
      time: parts[1]?.substring(0, 5) || ''
    };
  };

  const initialDateTime = parseDateTime(component?.start_datetime);

  // Pickup state
  const [pickupType, setPickupType] = useState(getInitialPickupType());
  const [pickupLocation, setPickupLocation] = useState(null);
  const [pickupSearch, setPickupSearch] = useState(
    formatFullAddress(
      componentData.pickup_type,
      componentData.pickup_location,
      componentData.pickup_address,
      componentData.pickup_airport_code || componentData.airport_code
    )
  );
  const [pickupSearchResults, setPickupSearchResults] = useState([]);
  const [pickupSearchLoading, setPickupSearchLoading] = useState(false);
  const [showPickupDropdown, setShowPickupDropdown] = useState(false);
  const [pickupAddressDate, setPickupAddressDate] = useState(initialDateTime.date);
  const [pickupAddressTime, setPickupAddressTime] = useState(initialDateTime.time);

  // Airport pickup details - check both new and legacy field names
  const [pickupAirline, setPickupAirline] = useState(componentData.pickup_airline || componentData.airline || '');
  const [pickupAirlineCode, setPickupAirlineCode] = useState(componentData.pickup_airline_code || componentData.airline_code || '');
  const [pickupFlightNumber, setPickupFlightNumber] = useState(componentData.pickup_flight_number || componentData.flight_number || '');
  const [pickupDate, setPickupDate] = useState(componentData.pickup_flight_date || componentData.flight_date || initialDateTime.date);
  const [pickupTime, setPickupTime] = useState(componentData.pickup_flight_time || componentData.flight_time || initialDateTime.time);
  const [pickupOriginAirport, setPickupOriginAirport] = useState(componentData.pickup_origin_airport || componentData.flight_origin || '');
  const [pickupOriginAirportCode, setPickupOriginAirportCode] = useState(componentData.pickup_origin_airport || componentData.flight_origin || '');
  const [airlineSearchResults, setAirlineSearchResults] = useState([]);
  const [showAirlineDropdown, setShowAirlineDropdown] = useState(false);
  const [originAirportSearchResults, setOriginAirportSearchResults] = useState([]);
  const [showOriginAirportDropdown, setShowOriginAirportDropdown] = useState(false);

  // Dropoff state
  const [dropoffType, setDropoffType] = useState(getInitialDropoffType());
  const [dropoffLocation, setDropoffLocation] = useState(null);
  const [dropoffSearch, setDropoffSearch] = useState(
    formatFullAddress(
      componentData.dropoff_type,
      componentData.dropoff_location,
      componentData.dropoff_address,
      componentData.dropoff_airport_code
    )
  );
  const [dropoffSearchResults, setDropoffSearchResults] = useState([]);
  const [dropoffSearchLoading, setDropoffSearchLoading] = useState(false);
  const [showDropoffDropdown, setShowDropoffDropdown] = useState(false);

  // Airport dropoff details
  const [dropoffAirline, setDropoffAirline] = useState(componentData.dropoff_airline || '');
  const [dropoffAirlineCode, setDropoffAirlineCode] = useState(componentData.dropoff_airline_code || '');
  const [dropoffFlightNumber, setDropoffFlightNumber] = useState(componentData.dropoff_flight_number || '');
  const [dropoffDate, setDropoffDate] = useState(componentData.dropoff_flight_date || '');
  const [dropoffTime, setDropoffTime] = useState(componentData.dropoff_flight_time || '');
  const [dropoffDestinationAirport, setDropoffDestinationAirport] = useState(componentData.dropoff_destination_airport || '');
  const [dropoffDestinationAirportCode, setDropoffDestinationAirportCode] = useState(componentData.dropoff_destination_airport || '');
  const [dropoffAirlineSearchResults, setDropoffAirlineSearchResults] = useState([]);
  const [showDropoffAirlineDropdown, setShowDropoffAirlineDropdown] = useState(false);
  const [destinationAirportSearchResults, setDestinationAirportSearchResults] = useState([]);
  const [showDestinationAirportDropdown, setShowDestinationAirportDropdown] = useState(false);

  // Initialize passengers from component data
  const initializePassengers = () => {
    // First check for the passengers array
    if (componentData.passengers && Array.isArray(componentData.passengers) && componentData.passengers.length > 0) {
      return componentData.passengers.map(p => ({
        firstName: p.name?.split(' ')[0] || '',
        lastName: p.name?.split(' ').slice(1).join(' ') || '',
        email: p.email || '',
        mobile: p.phone || '',
        vip: p.vip || false,
        isExecutive: p.isExecutive || false,
        executiveId: p.executiveId || null
      }));
    }
    // Fall back to legacy single passenger fields
    if (componentData.passenger_name) {
      const nameParts = componentData.passenger_name.split(' ');
      return [{
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        email: componentData.passenger_email || '',
        mobile: componentData.passenger_phone || '',
        vip: componentData.passenger_vip || false,
        isExecutive: false,
        executiveId: componentData.passenger_executive_id || null
      }];
    }
    return [];
  };

  // Passenger state
  const [passengers, setPassengers] = useState(initializePassengers());
  const [executiveSearch, setExecutiveSearch] = useState('');
  const [executiveSearchResults, setExecutiveSearchResults] = useState([]);
  const [showExecutiveDropdown, setShowExecutiveDropdown] = useState(false);
  const [manualPassenger, setManualPassenger] = useState({
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    vip: false
  });
  const [showManualForm, setShowManualForm] = useState(false);

  // Initialize stops from component data
  const initializeStops = () => {
    if (componentData.stops && Array.isArray(componentData.stops)) {
      return componentData.stops.map(s => ({
        type: s.type || 'address',
        location: s.location ? { name: s.location, id: s.location_id } : null,
        search: formatStopAddress(s),
        duration: s.duration_minutes ? String(s.duration_minutes) : '',
        comments: s.comments || s.comment || '',
        searchResults: [],
        showDropdown: false,
        searchLoading: false
      }));
    }
    return [];
  };

  // Stops state
  const [stops, setStops] = useState(initializeStops());

  // Stop type options
  const STOP_TYPES = [
    { value: 'address', label: 'Address' },
    { value: 'airport', label: 'Airport' },
    { value: 'train_station', label: 'Train Station' },
    { value: 'fbo', label: 'FBO (Private Aviation)' },
    { value: 'as_directed', label: 'As Directed' }
  ];

  // Duration options
  const DURATION_OPTIONS = [
    { value: '', label: 'No wait time' },
    { value: '15', label: '15 minutes' },
    { value: '30', label: '30 minutes' },
    { value: '45', label: '45 minutes' },
    { value: '60', label: '1 hour' },
    { value: '90', label: '1 hour 30 min' },
    { value: '120', label: '2 hours' },
    { value: '180', label: '3 hours' },
    { value: '240', label: '4 hours' }
  ];

  // Time options
  const TIME_OPTIONS = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const hourStr = String(hour).padStart(2, '0');
      const minuteStr = String(minute).padStart(2, '0');
      const value = `${hourStr}:${minuteStr}`;
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour < 12 ? 'AM' : 'PM';
      const label = `${hour12}:${minuteStr} ${ampm}`;
      TIME_OPTIONS.push({ value, label });
    }
  }

  // Comments
  const [clientComments, setClientComments] = useState(componentData.special_requests || component?.notes || '');
  const [pickupComments, setPickupComments] = useState(componentData.pickup_comments || '');
  const [dropoffComments, setDropoffComments] = useState(componentData.dropoff_comments || '');

  // Update and error state
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');

  // Urbanride data fetching state
  const [urbanrideLoading, setUrbanrideLoading] = useState(false);
  const [urbanrideError, setUrbanrideError] = useState('');

  // Refs for debouncing
  const pickupSearchTimeout = useRef(null);
  const dropoffSearchTimeout = useRef(null);
  const airlineSearchTimeout = useRef(null);
  const dropoffAirlineSearchTimeout = useRef(null);
  const originAirportSearchTimeout = useRef(null);
  const destinationAirportSearchTimeout = useRef(null);
  const stopSearchTimeouts = useRef({});

  // Selection refs to prevent dropdown reopening
  const pickupSelectionMade = useRef(false);
  const dropoffSelectionMade = useRef(false);
  const airlineSelectionMade = useRef(false);
  const dropoffAirlineSelectionMade = useRef(false);
  const originAirportSelectionMade = useRef(false);
  const destinationAirportSelectionMade = useRef(false);

  // Initialize API and fetch Urbanride data on mount
  useEffect(() => {
    initApiAndFetchData();
  }, []);

  const initApiAndFetchData = async () => {
    setConfigLoading(true);
    const result = await initializeUrbanrideApi();
    setApiConfigured(result.configured);
    setConfigLoading(false);

    // If API is configured and we have a URCN, fetch the trip details from Urbanride
    const urcn = component?.confirmation_number || componentData.urbanride_urcn || componentData.confirmation_number;
    if (result.configured && urcn) {
      await fetchUrbanrideData(urcn);
    }
  };

  // Fetch trip details from Urbanride API and populate form fields
  const fetchUrbanrideData = async (urcn) => {
    setUrbanrideLoading(true);
    setUrbanrideError('');

    try {
      const tripData = await getTripDetails(urcn);

      if (tripData) {
        populateFromUrbanrideData(tripData);
      }
    } catch (err) {
      console.error('Failed to fetch Urbanride trip details:', err);
      setUrbanrideError(`Could not fetch booking details from Urbanride: ${err.message}`);
      // Don't throw - we'll fall back to component_data values
    } finally {
      setUrbanrideLoading(false);
    }
  };

  // Populate form fields from Urbanride API response
  const populateFromUrbanrideData = (tripData) => {
    // Helper function to fetch address details from a Google Place ID using backend proxy
    // This bypasses client-side API key restrictions by making the call through our server
    const fetchAddressFromPlaceId = async (placeId) => {
      if (!placeId) {
        return null;
      }

      try {
        const response = await axios.get(`/google-places/details/${encodeURIComponent(placeId)}`);

        if (response.data.success) {
          return {
            id: placeId,
            placeId: placeId,
            name: response.data.name || '',
            details: response.data.formatted_address || response.data.name || placeId,
            formatted_address: response.data.formatted_address || ''
          };
        } else {
          return null;
        }
      } catch (err) {
        return null;
      }
    };

    // Parse pickup data
    if (tripData.pickup) {
      const pickup = tripData.pickup;

      if (pickup.type === 'airport' && pickup.airport) {
        setPickupType('airport');

        // Set airport display name - search for full name using the code
        const airportCode = pickup.airport.airport || '';
        setPickupSearch(airportCode);
        setPickupLocation({ id: airportCode, name: airportCode, type: 'airport' });

        // Fetch airport full name
        if (airportCode) {
          searchLocations({ input: airportCode, type: 'airport' }).then(result => {
            const match = result.locations?.find(l => l.id === airportCode);
            if (match) {
              setPickupSearch(`${match.id} - ${match.name}`);
              setPickupLocation(match);
            }
          }).catch(console.error);
        }

        // Set airline - search for full name using the code
        const airlineCode = pickup.airport.airline || '';
        if (airlineCode) {
          setPickupAirlineCode(airlineCode);
          // Fetch airline full name
          searchAirlines({ input: airlineCode }).then(result => {
            const match = result.airlines?.find(a => a.code === airlineCode);
            if (match) {
              setPickupAirline(`${match.code} - ${match.name}`);
            } else {
              setPickupAirline(airlineCode);
            }
          }).catch(() => setPickupAirline(airlineCode));
        }

        // Set flight number
        setPickupFlightNumber(pickup.airport.flightNumber || '');

        // Parse datetime
        if (pickup.airport.dateTime) {
          const [date, time] = pickup.airport.dateTime.split('T');
          setPickupDate(date || '');
          setPickupTime(time?.substring(0, 5) || '');
        }

        // Set origin airport (otherAirport in Urbanride = origin for pickup)
        const originCode = pickup.airport.otherAirport || '';
        if (originCode) {
          setPickupOriginAirportCode(originCode);
          // Fetch origin airport full name
          searchLocations({ input: originCode, type: 'airport' }).then(result => {
            const match = result.locations?.find(l => l.id === originCode);
            if (match) {
              setPickupOriginAirport(`${match.id} - ${match.name}`);
            } else {
              setPickupOriginAirport(originCode);
            }
          }).catch(() => setPickupOriginAirport(originCode));
        }
      } else if (pickup.type === 'address') {
        setPickupType('address');

        // The place ID is at the top level (pickup.id), not in pickup.address.id
        const placeId = pickup.id || pickup.address?.placeId || pickup.address?.id || '';

        // Parse datetime from address
        if (pickup.address?.dateTime) {
          const [date, time] = pickup.address.dateTime.split('T');
          setPickupAddressDate(date || '');
          setPickupAddressTime(time?.substring(0, 5) || '');
        }

        // Fetch the actual address using the place ID
        if (placeId) {
          setPickupLocation({ id: placeId, placeId: placeId });
          fetchAddressFromPlaceId(placeId).then(addressData => {
            if (addressData) {
              setPickupSearch(addressData.details || addressData.name || placeId);
              setPickupLocation(addressData);
            } else {
              setPickupSearch(placeId);
            }
          });
        }
      }
    }

    // Parse dropoff data
    if (tripData.dropoff) {
      const dropoff = tripData.dropoff;

      if (dropoff.type === 'airport' && dropoff.airport) {
        setDropoffType('airport');

        // Set airport display name
        const airportCode = dropoff.airport.airport || '';
        setDropoffSearch(airportCode);
        setDropoffLocation({ id: airportCode, name: airportCode, type: 'airport' });

        // Fetch airport full name
        if (airportCode) {
          searchLocations({ input: airportCode, type: 'airport' }).then(result => {
            const match = result.locations?.find(l => l.id === airportCode);
            if (match) {
              setDropoffSearch(`${match.id} - ${match.name}`);
              setDropoffLocation(match);
            }
          }).catch(console.error);
        }

        // Set airline
        const airlineCode = dropoff.airport.airline || '';
        if (airlineCode) {
          setDropoffAirlineCode(airlineCode);
          searchAirlines({ input: airlineCode }).then(result => {
            const match = result.airlines?.find(a => a.code === airlineCode);
            if (match) {
              setDropoffAirline(`${match.code} - ${match.name}`);
            } else {
              setDropoffAirline(airlineCode);
            }
          }).catch(() => setDropoffAirline(airlineCode));
        }

        // Set flight number
        setDropoffFlightNumber(dropoff.airport.flightNumber || '');

        // Parse datetime
        if (dropoff.airport.dateTime) {
          const [date, time] = dropoff.airport.dateTime.split('T');
          setDropoffDate(date || '');
          setDropoffTime(time?.substring(0, 5) || '');
        }

        // Set destination airport (otherAirport in Urbanride = destination for dropoff)
        const destCode = dropoff.airport.otherAirport || '';
        if (destCode) {
          setDropoffDestinationAirportCode(destCode);
          searchLocations({ input: destCode, type: 'airport' }).then(result => {
            const match = result.locations?.find(l => l.id === destCode);
            if (match) {
              setDropoffDestinationAirport(`${match.id} - ${match.name}`);
            } else {
              setDropoffDestinationAirport(destCode);
            }
          }).catch(() => setDropoffDestinationAirport(destCode));
        }
      } else if (dropoff.type === 'address') {
        setDropoffType('address');

        // The place ID is at the top level (dropoff.id), not in dropoff.address.id
        const placeId = dropoff.id || dropoff.address?.placeId || dropoff.address?.id || '';

        // Fetch the actual address using the place ID
        if (placeId) {
          setDropoffLocation({ id: placeId, placeId: placeId });
          fetchAddressFromPlaceId(placeId).then(addressData => {
            if (addressData) {
              setDropoffSearch(addressData.details || addressData.name || placeId);
              setDropoffLocation(addressData);
            } else {
              setDropoffSearch(placeId);
            }
          });
        }
      }
    }

    // Parse stops
    if (tripData.stops && Array.isArray(tripData.stops) && tripData.stops.length > 0) {
      // Create initial stops structure, then fetch addresses asynchronously
      const initialStops = tripData.stops.map((stop) => {
        // The place ID is at the top level (stop.id), not in stop.address.id
        const placeId = stop.id || stop.address?.placeId || stop.address?.id || '';

        return {
          type: stop.type || 'address',
          location: placeId ? { id: placeId, placeId: placeId } : null,
          search: placeId, // Will be updated after async fetch
          duration: '',
          comments: stop.address?.comments || stop.airport?.comments || '',
          searchResults: [],
          showDropdown: false,
          searchLoading: false
        };
      });

      setStops(initialStops);

      // Fetch address details for each stop with a small delay to ensure Google API is ready
      tripData.stops.forEach((stop, index) => {
        const placeId = stop.id || stop.address?.placeId || stop.address?.id || '';

        if (stop.type === 'airport' && stop.airport) {
          const airportCode = stop.airport.airport || '';
          if (airportCode) {
            searchLocations({ input: airportCode, type: 'airport' }).then(result => {
              const match = result.locations?.find(l => l.id === airportCode);
              if (match) {
                setStops(prev => {
                  const updated = [...prev];
                  if (updated[index]) {
                    updated[index] = {
                      ...updated[index],
                      search: `${match.id} - ${match.name}`,
                      location: match
                    };
                  }
                  return updated;
                });
              }
            }).catch(console.error);
          }
        } else if (placeId) {
          // Add a small delay to ensure Google Places API is fully available
          setTimeout(() => {
            fetchAddressFromPlaceId(placeId).then(addressData => {
              if (addressData) {
                setStops(prev => {
                  const updated = [...prev];
                  if (updated[index]) {
                    updated[index] = {
                      ...updated[index],
                      search: addressData.formatted_address || addressData.details || addressData.name || placeId,
                      location: addressData
                    };
                  }
                  return updated;
                });
              }
            }).catch(() => {});
          }, 100 * (index + 1)); // Stagger requests slightly
        }
      });
    }

    // Parse passengers
    if (tripData.passengers && Array.isArray(tripData.passengers) && tripData.passengers.length > 0) {
      const parsedPassengers = tripData.passengers.map(p => ({
        firstName: p.firstName || '',
        lastName: p.lastName || '',
        email: p.email || '',
        mobile: p.mobile || '',
        vip: p.vip || false,
        title: p.title || '',
        isExecutive: false,
        executiveId: null
      }));
      setPassengers(parsedPassengers);
    }

    // Parse client comments
    if (tripData.clientComments) {
      setClientComments(tripData.clientComments);
    }

    // Parse location comments from pickup/dropoff
    if (tripData.pickup) {
      const pc = tripData.pickup.airport?.comments || tripData.pickup.address?.comments || tripData.pickup.fbo?.comments || tripData.pickup.trainStation?.comments || '';
      if (pc) setPickupComments(pc);
    }
    if (tripData.dropoff) {
      const dc = tripData.dropoff.airport?.comments || tripData.dropoff.address?.comments || tripData.dropoff.fbo?.comments || tripData.dropoff.trainStation?.comments || '';
      if (dc) setDropoffComments(dc);
    }
  };

  // Search handlers
  const handlePickupSearch = useCallback(async (query) => {
    setPickupSearch(query);
    setPickupLocation(null);
    pickupSelectionMade.current = false;

    if (pickupSearchTimeout.current) {
      clearTimeout(pickupSearchTimeout.current);
    }

    if (!query || query.length < 2) {
      setPickupSearchResults([]);
      setShowPickupDropdown(false);
      return;
    }

    pickupSearchTimeout.current = setTimeout(async () => {
      try {
        setPickupSearchLoading(true);
        const result = await searchLocations({
          input: query,
          type: pickupType === 'airport' ? 'airport' : 'address'
        });
        if (!pickupSelectionMade.current) {
          setPickupSearchResults(result.locations || []);
          if (result.locations?.length > 0) {
            setShowPickupDropdown(true);
          }
        }
      } catch (err) {
        console.error('Pickup search failed:', err);
      } finally {
        setPickupSearchLoading(false);
      }
    }, 300);
  }, [pickupType]);

  const handleDropoffSearch = useCallback(async (query) => {
    setDropoffSearch(query);
    setDropoffLocation(null);
    dropoffSelectionMade.current = false;

    if (dropoffSearchTimeout.current) {
      clearTimeout(dropoffSearchTimeout.current);
    }

    if (!query || query.length < 2) {
      setDropoffSearchResults([]);
      setShowDropoffDropdown(false);
      return;
    }

    dropoffSearchTimeout.current = setTimeout(async () => {
      try {
        setDropoffSearchLoading(true);
        const result = await searchLocations({
          input: query,
          type: dropoffType === 'airport' ? 'airport' : 'address'
        });
        if (!dropoffSelectionMade.current) {
          setDropoffSearchResults(result.locations || []);
          if (result.locations?.length > 0) {
            setShowDropoffDropdown(true);
          }
        }
      } catch (err) {
        console.error('Dropoff search failed:', err);
      } finally {
        setDropoffSearchLoading(false);
      }
    }, 300);
  }, [dropoffType]);

  const handleAirlineSearch = useCallback(async (query, isDropoff = false) => {
    const setAirline = isDropoff ? setDropoffAirline : setPickupAirline;
    const setCode = isDropoff ? setDropoffAirlineCode : setPickupAirlineCode;
    const setResults = isDropoff ? setDropoffAirlineSearchResults : setAirlineSearchResults;
    const setShow = isDropoff ? setShowDropoffAirlineDropdown : setShowAirlineDropdown;
    const timeout = isDropoff ? dropoffAirlineSearchTimeout : airlineSearchTimeout;
    const selectionRef = isDropoff ? dropoffAirlineSelectionMade : airlineSelectionMade;

    setAirline(query);
    setCode('');
    selectionRef.current = false;

    if (timeout.current) {
      clearTimeout(timeout.current);
    }

    if (!query || query.length < 2) {
      setResults([]);
      setShow(false);
      return;
    }

    timeout.current = setTimeout(async () => {
      try {
        const result = await searchAirlines({ input: query });
        if (!selectionRef.current) {
          setResults(result.airlines || []);
          if (result.airlines?.length > 0) {
            setShow(true);
          }
        }
      } catch (err) {
        console.error('Airline search failed:', err);
      }
    }, 200);
  }, []);

  const handleOriginAirportSearch = useCallback(async (query) => {
    setPickupOriginAirport(query);
    setPickupOriginAirportCode('');
    originAirportSelectionMade.current = false;

    if (originAirportSearchTimeout.current) {
      clearTimeout(originAirportSearchTimeout.current);
    }

    if (!query || query.length < 2) {
      setOriginAirportSearchResults([]);
      setShowOriginAirportDropdown(false);
      return;
    }

    originAirportSearchTimeout.current = setTimeout(async () => {
      try {
        const result = await searchLocations({ input: query, type: 'airport' });
        if (!originAirportSelectionMade.current) {
          setOriginAirportSearchResults(result.locations || []);
          if (result.locations?.length > 0) {
            setShowOriginAirportDropdown(true);
          }
        }
      } catch (err) {
        console.error('Origin airport search failed:', err);
      }
    }, 300);
  }, []);

  const handleDestinationAirportSearch = useCallback(async (query) => {
    setDropoffDestinationAirport(query);
    setDropoffDestinationAirportCode('');
    destinationAirportSelectionMade.current = false;

    if (destinationAirportSearchTimeout.current) {
      clearTimeout(destinationAirportSearchTimeout.current);
    }

    if (!query || query.length < 2) {
      setDestinationAirportSearchResults([]);
      setShowDestinationAirportDropdown(false);
      return;
    }

    destinationAirportSearchTimeout.current = setTimeout(async () => {
      try {
        const result = await searchLocations({ input: query, type: 'airport' });
        if (!destinationAirportSelectionMade.current) {
          setDestinationAirportSearchResults(result.locations || []);
          if (result.locations?.length > 0) {
            setShowDestinationAirportDropdown(true);
          }
        }
      } catch (err) {
        console.error('Destination airport search failed:', err);
      }
    }, 300);
  }, []);

  // Selection handlers
  const selectPickupLocation = (location) => {
    pickupSelectionMade.current = true;
    if (pickupSearchTimeout.current) {
      clearTimeout(pickupSearchTimeout.current);
    }
    setPickupLocation(location);
    setPickupSearch(location.name);
    setPickupSearchResults([]);
    setShowPickupDropdown(false);
  };

  const selectDropoffLocation = (location) => {
    dropoffSelectionMade.current = true;
    if (dropoffSearchTimeout.current) {
      clearTimeout(dropoffSearchTimeout.current);
    }
    setDropoffLocation(location);
    setDropoffSearch(location.name);
    setDropoffSearchResults([]);
    setShowDropoffDropdown(false);
  };

  const selectAirline = (airline, isDropoff = false) => {
    if (isDropoff) {
      dropoffAirlineSelectionMade.current = true;
      if (dropoffAirlineSearchTimeout.current) {
        clearTimeout(dropoffAirlineSearchTimeout.current);
      }
      setDropoffAirline(airline.name);
      setDropoffAirlineCode(airline.code);
      setDropoffAirlineSearchResults([]);
      setShowDropoffAirlineDropdown(false);
    } else {
      airlineSelectionMade.current = true;
      if (airlineSearchTimeout.current) {
        clearTimeout(airlineSearchTimeout.current);
      }
      setPickupAirline(airline.name);
      setPickupAirlineCode(airline.code);
      setAirlineSearchResults([]);
      setShowAirlineDropdown(false);
    }
  };

  const selectOriginAirport = (location) => {
    originAirportSelectionMade.current = true;
    if (originAirportSearchTimeout.current) {
      clearTimeout(originAirportSearchTimeout.current);
    }
    const airportCode = location.id || location.name.match(/\(([A-Z]{3})\)/)?.[1] || '';
    setPickupOriginAirport(location.name);
    setPickupOriginAirportCode(airportCode);
    setOriginAirportSearchResults([]);
    setShowOriginAirportDropdown(false);
  };

  const selectDestinationAirport = (location) => {
    destinationAirportSelectionMade.current = true;
    if (destinationAirportSearchTimeout.current) {
      clearTimeout(destinationAirportSearchTimeout.current);
    }
    const airportCode = location.id || location.name.match(/\(([A-Z]{3})\)/)?.[1] || '';
    setDropoffDestinationAirport(location.name);
    setDropoffDestinationAirportCode(airportCode);
    setDestinationAirportSearchResults([]);
    setShowDestinationAirportDropdown(false);
  };

  // Executive search
  const handleExecutiveSearch = (query) => {
    setExecutiveSearch(query);
    if (!query || query.length < 2) {
      setExecutiveSearchResults([]);
      setShowExecutiveDropdown(false);
      return;
    }

    const allExecs = [...(tripExecutives || []), ...(allExecutives || [])];
    const uniqueExecs = allExecs.filter((exec, index, self) =>
      index === self.findIndex(e => e.id === exec.id)
    );

    const queryLower = query.toLowerCase();
    const filtered = uniqueExecs.filter(exec => {
      const fullName = `${exec.first_name || ''} ${exec.last_name || ''}`.toLowerCase();
      const email = (exec.email || '').toLowerCase();
      return fullName.includes(queryLower) || email.includes(queryLower);
    });

    setExecutiveSearchResults(filtered);
    setShowExecutiveDropdown(filtered.length > 0);
  };

  const addExecutiveAsPassenger = (exec) => {
    const alreadyAdded = passengers.some(p => p.executiveId === exec.id);
    if (alreadyAdded) {
      setError('This executive is already added as a passenger');
      setTimeout(() => setError(''), 3000);
      return;
    }

    const newPassenger = {
      title: exec.title || '',
      firstName: exec.first_name || '',
      lastName: exec.last_name || '',
      email: exec.email || '',
      mobile: exec.phone || exec.mobile || '',
      vip: exec.vip || false,
      isExecutive: true,
      executiveId: exec.id
    };

    setPassengers([...passengers, newPassenger]);
    setExecutiveSearch('');
    setExecutiveSearchResults([]);
    setShowExecutiveDropdown(false);
  };

  const addManualPassenger = () => {
    if (!manualPassenger.firstName || !manualPassenger.lastName) {
      setError('Please fill in all required passenger fields');
      return;
    }

    setPassengers([...passengers, { ...manualPassenger, isExecutive: false }]);
    setManualPassenger({ title: '', firstName: '', lastName: '', email: '', mobile: '', vip: false });
    setShowManualForm(false);
  };

  const removePassenger = (index) => {
    setPassengers(passengers.filter((_, i) => i !== index));
  };

  // Stop handlers
  const addStop = () => {
    setStops([...stops, {
      type: 'address',
      location: null,
      search: '',
      duration: '',
      comments: '',
      searchResults: [],
      showDropdown: false,
      searchLoading: false
    }]);
  };

  const removeStop = (index) => {
    setStops(stops.filter((_, i) => i !== index));
  };

  const updateStopType = (index, type) => {
    const newStops = [...stops];
    newStops[index].type = type;
    newStops[index].location = null;
    newStops[index].search = '';
    setStops(newStops);
  };

  const updateStopDuration = (index, duration) => {
    const newStops = [...stops];
    newStops[index].duration = duration;
    setStops(newStops);
  };

  const handleStopSearch = async (index, query) => {
    const newStops = [...stops];
    newStops[index].search = query;
    newStops[index].location = null;
    setStops(newStops);

    if (stopSearchTimeouts.current[index]) {
      clearTimeout(stopSearchTimeouts.current[index]);
    }

    if (!query || query.length < 2) {
      newStops[index].searchResults = [];
      newStops[index].showDropdown = false;
      setStops([...newStops]);
      return;
    }

    stopSearchTimeouts.current[index] = setTimeout(async () => {
      try {
        const updatedStops = [...stops];
        updatedStops[index].searchLoading = true;
        setStops(updatedStops);

        const locationType = stops[index].type === 'airport' ? 'airport' : 'address';
        const result = await searchLocations({ input: query, type: locationType });

        const finalStops = [...stops];
        finalStops[index].searchResults = result.locations || [];
        finalStops[index].showDropdown = result.locations?.length > 0;
        finalStops[index].searchLoading = false;
        setStops(finalStops);
      } catch (err) {
        console.error('Stop search failed:', err);
        const finalStops = [...stops];
        finalStops[index].searchLoading = false;
        setStops(finalStops);
      }
    }, 300);
  };

  const selectStopLocation = (index, location) => {
    const newStops = [...stops];
    newStops[index].location = location;
    newStops[index].search = location.name;
    newStops[index].searchResults = [];
    newStops[index].showDropdown = false;
    setStops(newStops);
  };

  const setStopDropdownVisible = (index, visible) => {
    const newStops = [...stops];
    newStops[index].showDropdown = visible;
    setStops(newStops);
  };

  // Validate flight
  const handleValidateFlight = async (isDropoff = false) => {
    const airport = isDropoff
      ? (dropoffLocation?.id || dropoffSearch.toUpperCase().substring(0, 3))
      : (pickupLocation?.id || pickupSearch.toUpperCase().substring(0, 3));
    const airline = isDropoff ? dropoffAirlineCode : pickupAirlineCode;
    const flightNumber = isDropoff ? dropoffFlightNumber : pickupFlightNumber;
    const date = isDropoff ? dropoffDate : pickupDate;
    const time = isDropoff ? dropoffTime : pickupTime;

    if (!airport || !airline || !flightNumber || !date) {
      setError('Please fill in airport, airline, flight number, and date before validating');
      return;
    }

    const dateTime = time ? `${date}T${time}` : `${date}T12:00`;

    try {
      setError('');
      const result = await validateFlight({
        airport: airport.toUpperCase(),
        airline: airline.toUpperCase(),
        flightNumber,
        dateTime,
        departure: isDropoff
      });

      const flightData = result?.flight || result;
      const verifiedTime = isDropoff
        ? (flightData?.departureTimeScheduled || flightData?.departureTime || flightData?.scheduledTime)
        : (flightData?.arrivalTimeScheduled || flightData?.arrivalTime || flightData?.scheduledTime);

      if (result && verifiedTime) {
        const scheduledDateTime = new Date(verifiedTime);
        const hours = String(scheduledDateTime.getHours()).padStart(2, '0');
        const minutes = String(scheduledDateTime.getMinutes()).padStart(2, '0');
        const exactTime = `${hours}:${minutes}`;

        if (isDropoff) {
          setDropoffTime(exactTime);
          if (flightData?.destinationAirport && !dropoffDestinationAirportCode) {
            setDropoffDestinationAirportCode(flightData.destinationAirport);
            setDropoffDestinationAirport(flightData.destinationAirport);
          }
        } else {
          setPickupTime(exactTime);
          if (flightData?.departureAirport && !pickupOriginAirportCode) {
            setPickupOriginAirportCode(flightData.departureAirport);
            setPickupOriginAirport(flightData.departureAirport);
          }
        }

        setError(`Flight validated! ${isDropoff ? 'Departure' : 'Arrival'} time updated to ${exactTime}.`);
        setTimeout(() => setError(''), 5000);
      } else {
        setError('Flight validated successfully!');
        setTimeout(() => setError(''), 3000);
      }
    } catch (err) {
      setError(`Flight validation failed: ${err.message}`);
    }
  };

  // Handle save/update
  const handleSave = async () => {
    setError('');

    // Validate required fields
    if (!pickupSearch) {
      setError('Please enter a pickup location');
      return;
    }
    if (!dropoffSearch) {
      setError('Please enter a dropoff location');
      return;
    }

    // Determine service type
    let serviceType = 'transfer';
    if (pickupType === 'airport') {
      serviceType = 'airport_pickup';
    } else if (dropoffType === 'airport') {
      serviceType = 'airport_dropoff';
    }

    // Build start datetime
    const startDateTime = pickupType === 'airport'
      ? `${pickupDate}T${pickupTime || '12:00'}`
      : `${pickupAddressDate}T${pickupAddressTime || '12:00'}`;

    // Build end datetime
    let endDateTime = null;
    if (dropoffType === 'airport' && dropoffDate && dropoffTime) {
      endDateTime = `${dropoffDate}T${dropoffTime}`;
    }

    // Build component title
    const title = `Urbanride: ${pickupSearch} to ${dropoffSearch}`;
    const location = `${pickupSearch} to ${dropoffSearch}`;

    // Build passenger names
    const passengerNames = passengers.map(p => `${p.firstName} ${p.lastName}`).join(', ');

    try {
      setIsUpdating(true);

      const updateData = {
        component_type: 'chauffeur_service',
        title: title,
        description: `${componentData.vehicle_class || 'Vehicle'} - ${passengerNames || 'No passengers'}`,
        start_datetime: startDateTime,
        end_datetime: endDateTime || startDateTime,
        location: location,
        status: component.status || 'confirmed',
        confirmation_number: component.confirmation_number,
        notes: clientComments,
        component_data: {
          ...componentData,
          service_type: serviceType,
          // Preserve original pickup location/address if user didn't select a new one
          pickup_location: pickupLocation?.name || componentData.pickup_location,
          pickup_address: pickupLocation?.details || pickupLocation?.address || componentData.pickup_address,
          // Preserve original dropoff location/address if user didn't select a new one
          dropoff_location: dropoffLocation?.name || componentData.dropoff_location,
          dropoff_address: dropoffLocation?.details || dropoffLocation?.address || componentData.dropoff_address,
          pickup_type: pickupType,
          dropoff_type: dropoffType,
          // Pickup flight details
          ...(pickupType === 'airport' && {
            pickup_airport_code: pickupLocation?.id || componentData.pickup_airport_code,
            pickup_airline: pickupAirline,
            pickup_airline_code: pickupAirlineCode,
            pickup_flight_number: pickupFlightNumber,
            pickup_flight_date: pickupDate,
            pickup_flight_time: pickupTime,
            pickup_origin_airport: pickupOriginAirportCode || pickupOriginAirport
          }),
          // Dropoff flight details
          ...(dropoffType === 'airport' && {
            dropoff_airport_code: dropoffLocation?.id || componentData.dropoff_airport_code,
            dropoff_airline: dropoffAirline,
            dropoff_airline_code: dropoffAirlineCode,
            dropoff_flight_number: dropoffFlightNumber,
            dropoff_flight_date: dropoffDate,
            dropoff_flight_time: dropoffTime,
            dropoff_destination_airport: dropoffDestinationAirportCode || dropoffDestinationAirport
          }),
          // Location comments
          pickup_comments: pickupComments || '',
          dropoff_comments: dropoffComments || '',
          // Stops - preserve original location_address if not changed
          stops: stops.filter(s => s.location || s.search).map((s, idx) => {
            const originalStop = componentData.stops?.[idx] || {};
            return {
              type: s.type,
              location: s.location?.name || originalStop.location || s.search,
              location_address: s.location?.details || s.location?.address || originalStop.location_address,
              location_id: s.location?.id || originalStop.location_id || '',
              duration_minutes: s.duration ? parseInt(s.duration) : null,
              comments: s.comments || ''
            };
          }),
          // Passengers
          passengers: passengers.map(p => ({
            name: `${p.firstName} ${p.lastName}`,
            email: p.email,
            phone: p.mobile,
            vip: p.vip,
            isExecutive: p.isExecutive,
            executiveId: p.executiveId || null
          })),
          // Legacy single passenger fields
          passenger_name: passengers.length > 0 ? `${passengers[0].firstName} ${passengers[0].lastName}` : '',
          passenger_email: passengers.length > 0 ? passengers[0].email : '',
          passenger_phone: passengers.length > 0 ? passengers[0].mobile : '',
          // Special requests
          special_requests: clientComments
        }
      };

      const apiBase = getApiBase();
      await axios.put(`${apiBase}/trips/${tripId}/components/${component.id}`, updateData, {
        headers: getAuthHeaders()
      });

      onSuccess();
    } catch (err) {
      console.error('Update failed:', err);
      setError(`Failed to update: ${err.response?.data?.detail || err.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  // Loading state
  if (configLoading || urbanrideLoading) {
    return (
      <div className="fixed inset-0 bg-ui-overlay/40 flex items-center justify-center z-[70]">
        <div className="bg-ui-surface rounded-lg p-6 max-w-4xl w-full mx-4">
          <div className="flex flex-col items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ui-primary"></div>
            {urbanrideLoading && (
              <p className="mt-4 text-ui-ink-3">Loading booking details from Urbanride...</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-ui-overlay/40 flex items-center justify-center z-[70]">
      <div className="bg-ui-surface rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-ui-ink">Edit Chauffeured Service</h3>
            <p className="text-sm text-ui-muted mt-1">Update booking details</p>
          </div>
          <button onClick={onClose} className="text-ui-muted hover:text-ui-ink-2 text-xl">
            &times;
          </button>
        </div>

        {error && (
          <div className={`mb-4 p-3 rounded-md ${error.includes('validated') ? 'bg-ui-green-bg border border-ui-green-line text-ui-green-fg' : 'bg-ui-red-bg border border-ui-red-line text-ui-red-fg'}`}>
            {error}
          </div>
        )}

        {urbanrideError && (
          <div className="mb-4 p-3 rounded-md bg-ui-amber-bg border border-ui-amber-line text-ui-amber-fg">
            <strong>Note:</strong> {urbanrideError}. Data shown may be from saved component data.
          </div>
        )}

        <div className="space-y-6">
          {/* Pickup Section */}
          <div className="bg-ui-sunk rounded-lg p-4">
            <h4 className="text-md font-semibold text-ui-ink mb-4">Pickup</h4>

            <div className="mb-4">
              <div className="flex items-center gap-4 mb-3">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="pickupType"
                    checked={pickupType === 'airport'}
                    onChange={() => { setPickupType('airport'); setPickupLocation(null); }}
                  />
                  <span className="text-sm">Airport</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="pickupType"
                    checked={pickupType === 'address'}
                    onChange={() => { setPickupType('address'); setPickupLocation(null); }}
                  />
                  <span className="text-sm">Address</span>
                </label>
              </div>

              {/* Location Search */}
              <div className="relative">
                <input
                  type="text"
                  value={pickupSearch}
                  onChange={(e) => handlePickupSearch(e.target.value)}
                  onFocus={() => pickupSearchResults.length > 0 && setShowPickupDropdown(true)}
                  placeholder={pickupType === 'airport' ? 'Search airport (e.g., JFK, Los Angeles)' : 'Search address'}
                  className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                />
                {pickupSearchLoading && (
                  <div className="absolute right-3 top-2.5">
                    <div className="animate-spin h-5 w-5 border-2 border-ui-primary rounded-full border-t-transparent"></div>
                  </div>
                )}
                {showPickupDropdown && pickupSearchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-ui-surface border border-ui-line rounded-md shadow-lg max-h-48 overflow-auto">
                    {pickupSearchResults.map((loc, idx) => (
                      <button
                        key={idx}
                        className="w-full px-3 py-2 text-left hover:bg-ui-hover border-b border-ui-divider last:border-0"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectPickupLocation(loc)}
                      >
                        <div className="font-medium text-ui-ink text-sm">{loc.name}</div>
                        {loc.details && <div className="text-xs text-ui-muted">{loc.details}</div>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Airport-specific fields */}
            {pickupType === 'airport' && (
              <div className="grid md:grid-cols-2 gap-3">
                <div className="relative">
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Airline</label>
                  <input
                    type="text"
                    value={pickupAirline}
                    onChange={(e) => handleAirlineSearch(e.target.value, false)}
                    onFocus={() => airlineSearchResults.length > 0 && setShowAirlineDropdown(true)}
                    placeholder="Search airline"
                    className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                  />
                  {showAirlineDropdown && airlineSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-ui-surface border border-ui-line rounded-md shadow-lg max-h-32 overflow-auto">
                      {airlineSearchResults.map((airline, idx) => (
                        <button
                          key={idx}
                          className="w-full px-3 py-2 text-left hover:bg-ui-hover border-b border-ui-divider last:border-0 text-sm"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectAirline(airline, false)}
                        >
                          <span className="font-medium">{airline.code}</span> - {airline.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Flight Number</label>
                  <input
                    type="text"
                    value={pickupFlightNumber}
                    onChange={(e) => setPickupFlightNumber(e.target.value)}
                    placeholder="e.g., 123"
                    className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Arrival Date</label>
                  <DatePickerButton
                    label={pickupDate ? format(new Date(pickupDate), 'EEE, MMM d, yyyy') : 'Select date'}
                    selectedDate={pickupDate ? new Date(pickupDate) : undefined}
                    onPick={(date) => {
                      if (date) {
                        setPickupDate(format(date, 'yyyy-MM-dd'));
                      }
                    }}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Arrival Time</label>
                  <div className="flex gap-2">
                    <input
                      type="time"
                      value={pickupTime}
                      onChange={(e) => setPickupTime(e.target.value)}
                      className="flex-1 px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => handleValidateFlight(false)}
                      disabled={!pickupAirlineCode || !pickupFlightNumber || !pickupDate}
                      className="px-3 py-2 bg-ui-green-strong text-ui-on-primary text-xs rounded-md hover:bg-ui-green-strong/90 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      Validate
                    </button>
                  </div>
                </div>

                <div className="relative md:col-span-2">
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Flight Origin (optional)</label>
                  <input
                    type="text"
                    value={pickupOriginAirport}
                    onChange={(e) => handleOriginAirportSearch(e.target.value)}
                    onFocus={() => originAirportSearchResults.length > 0 && setShowOriginAirportDropdown(true)}
                    placeholder="Search airport"
                    className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                  />
                  {showOriginAirportDropdown && originAirportSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-ui-surface border border-ui-line rounded-md shadow-lg max-h-32 overflow-auto">
                      {originAirportSearchResults.map((loc, idx) => (
                        <button
                          key={idx}
                          className="w-full px-3 py-2 text-left hover:bg-ui-hover border-b border-ui-divider last:border-0 text-sm"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectOriginAirport(loc)}
                        >
                          {loc.id && <span className="text-ui-link mr-1">{loc.id}</span>} {loc.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Address-specific fields */}
            {pickupType === 'address' && (
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Pickup Date</label>
                  <DatePickerButton
                    label={pickupAddressDate ? format(new Date(pickupAddressDate), 'EEE, MMM d, yyyy') : 'Select date'}
                    selectedDate={pickupAddressDate ? new Date(pickupAddressDate) : undefined}
                    onPick={(date) => {
                      if (date) {
                        setPickupAddressDate(format(date, 'yyyy-MM-dd'));
                      }
                    }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Pickup Time</label>
                  <select
                    value={pickupAddressTime}
                    onChange={(e) => setPickupAddressTime(e.target.value)}
                    className="w-full h-10 px-3 border border-ui-line rounded-md bg-ui-surface text-sm focus:outline-none focus:ring-2 focus:ring-ui-primary"
                  >
                    <option value="">Select time</option>
                    {TIME_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {/* Pickup Comments */}
            <div className="mt-3">
              <label className="block text-xs font-medium text-ui-ink-2 mb-1">Pickup Comments (optional)</label>
              <input
                type="text"
                value={pickupComments}
                onChange={(e) => setPickupComments(e.target.value)}
                placeholder="Comments for pickup location..."
                className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
              />
            </div>
          </div>

          {/* Additional Stops Section */}
          <div className="bg-ui-sunk rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-md font-semibold text-ui-ink">Additional Stops (Optional)</h4>
              <button
                type="button"
                onClick={addStop}
                className="flex items-center gap-1 text-sm text-ui-link hover:text-ui-link"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Stop
              </button>
            </div>

            {stops.length === 0 ? (
              <p className="text-sm text-ui-muted">No additional stops. Click "Add Stop" to include stops between pickup and dropoff.</p>
            ) : (
              <div className="space-y-4">
                {stops.map((stop, index) => (
                  <div key={index} className="bg-ui-surface border border-ui-line rounded-lg p-3">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-ui-ink-2">Stop {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeStop(index)}
                        className="text-ui-red-fg hover:text-ui-red-fg p-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <div className="grid md:grid-cols-2 gap-3">
                      <div className={stop.type === 'as_directed' ? 'md:col-span-2' : ''}>
                        <label className="block text-xs font-medium text-ui-ink-2 mb-1">Stop Type</label>
                        <select
                          value={stop.type}
                          onChange={(e) => updateStopType(index, e.target.value)}
                          className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm bg-ui-surface"
                        >
                          {STOP_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Wait Time - Hidden for As Directed */}
                      {stop.type !== 'as_directed' && (
                        <div>
                          <label className="block text-xs font-medium text-ui-ink-2 mb-1">Wait Time</label>
                          <select
                            value={stop.duration}
                            onChange={(e) => updateStopDuration(index, e.target.value)}
                            className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm bg-ui-surface"
                          >
                            {DURATION_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* As Directed Info Message */}
                      {stop.type === 'as_directed' && (
                        <div className="md:col-span-2 bg-ui-blue-bg border border-ui-blue-line rounded-md p-3">
                          <div className="flex items-start gap-2">
                            <svg className="w-5 h-5 text-ui-link mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                              <p className="text-sm font-medium text-ui-blue-fg">Chauffeur Service - As Directed</p>
                              <p className="text-xs text-ui-link mt-1">Passenger/s will direct the driver to one or more stops during the journey.</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Location - Hidden for As Directed */}
                      {stop.type !== 'as_directed' && (
                        <div className="md:col-span-2 relative">
                          <label className="block text-xs font-medium text-ui-ink-2 mb-1">Location</label>
                          <input
                            type="text"
                            value={stop.search}
                            onChange={(e) => handleStopSearch(index, e.target.value)}
                            onFocus={() => stop.searchResults?.length > 0 && setStopDropdownVisible(index, true)}
                            placeholder="Search location"
                            className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                          />
                          {stop.showDropdown && stop.searchResults?.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-ui-surface border border-ui-line rounded-md shadow-lg max-h-40 overflow-auto">
                              {stop.searchResults.map((loc, locIdx) => (
                                <button
                                  key={locIdx}
                                  type="button"
                                  className="w-full px-3 py-2 text-left hover:bg-ui-hover border-b border-ui-divider last:border-0"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => selectStopLocation(index, loc)}
                                >
                                  <div className="font-medium text-ui-ink text-sm">{loc.name}</div>
                                  {loc.details && <div className="text-xs text-ui-muted">{loc.details}</div>}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Stop Comments */}
                      <div className="md:col-span-2">
                        <label className="block text-xs font-medium text-ui-ink-2 mb-1">Comments (optional)</label>
                        <input
                          type="text"
                          value={stop.comments || ''}
                          onChange={(e) => {
                            const updated = [...stops];
                            updated[index] = { ...updated[index], comments: e.target.value };
                            setStops(updated);
                          }}
                          placeholder="Comments for this stop..."
                          className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Dropoff Section */}
          <div className="bg-ui-sunk rounded-lg p-4">
            <h4 className="text-md font-semibold text-ui-ink mb-4">Dropoff</h4>

            <div className="mb-4">
              <div className="flex items-center gap-4 mb-3">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="dropoffType"
                    checked={dropoffType === 'airport'}
                    onChange={() => { setDropoffType('airport'); setDropoffLocation(null); }}
                  />
                  <span className="text-sm">Airport</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="dropoffType"
                    checked={dropoffType === 'address'}
                    onChange={() => { setDropoffType('address'); setDropoffLocation(null); }}
                  />
                  <span className="text-sm">Address</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="dropoffType"
                    checked={dropoffType === 'as_directed'}
                    onChange={() => { setDropoffType('as_directed'); setDropoffLocation(null); }}
                  />
                  <span className="text-sm">As Directed</span>
                </label>
              </div>

              {/* As Directed Info Message */}
              {dropoffType === 'as_directed' && (
                <div className="bg-ui-blue-bg border border-ui-blue-line rounded-md p-3">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-ui-link mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-ui-blue-fg">Chauffeur Service - As Directed</p>
                      <p className="text-xs text-ui-link mt-1">Passenger/s will direct the driver to the final destination.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Location Search - Hidden for As Directed */}
              {dropoffType !== 'as_directed' && (
                <div className="relative">
                  <input
                    type="text"
                    value={dropoffSearch}
                    onChange={(e) => handleDropoffSearch(e.target.value)}
                    onFocus={() => dropoffSearchResults.length > 0 && setShowDropoffDropdown(true)}
                    placeholder={dropoffType === 'airport' ? 'Search airport' : 'Search address'}
                    className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                  />
                  {dropoffSearchLoading && (
                    <div className="absolute right-3 top-2.5">
                      <div className="animate-spin h-5 w-5 border-2 border-ui-primary rounded-full border-t-transparent"></div>
                    </div>
                  )}
                  {showDropoffDropdown && dropoffSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-ui-surface border border-ui-line rounded-md shadow-lg max-h-48 overflow-auto">
                      {dropoffSearchResults.map((loc, idx) => (
                        <button
                          key={idx}
                          className="w-full px-3 py-2 text-left hover:bg-ui-hover border-b border-ui-divider last:border-0"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectDropoffLocation(loc)}
                        >
                          <div className="font-medium text-ui-ink text-sm">{loc.name}</div>
                          {loc.details && <div className="text-xs text-ui-muted">{loc.details}</div>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Airport-specific fields */}
            {dropoffType === 'airport' && (
              <div className="grid md:grid-cols-2 gap-3">
                <div className="relative">
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Airline</label>
                  <input
                    type="text"
                    value={dropoffAirline}
                    onChange={(e) => handleAirlineSearch(e.target.value, true)}
                    onFocus={() => dropoffAirlineSearchResults.length > 0 && setShowDropoffAirlineDropdown(true)}
                    placeholder="Search airline"
                    className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                  />
                  {showDropoffAirlineDropdown && dropoffAirlineSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-ui-surface border border-ui-line rounded-md shadow-lg max-h-32 overflow-auto">
                      {dropoffAirlineSearchResults.map((airline, idx) => (
                        <button
                          key={idx}
                          className="w-full px-3 py-2 text-left hover:bg-ui-hover border-b border-ui-divider last:border-0 text-sm"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectAirline(airline, true)}
                        >
                          <span className="font-medium">{airline.code}</span> - {airline.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Flight Number</label>
                  <input
                    type="text"
                    value={dropoffFlightNumber}
                    onChange={(e) => setDropoffFlightNumber(e.target.value)}
                    placeholder="e.g., 456"
                    className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Departure Date</label>
                  <DatePickerButton
                    label={dropoffDate ? format(new Date(dropoffDate), 'EEE, MMM d, yyyy') : 'Select date'}
                    selectedDate={dropoffDate ? new Date(dropoffDate) : undefined}
                    onPick={(date) => {
                      if (date) {
                        setDropoffDate(format(date, 'yyyy-MM-dd'));
                      }
                    }}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Departure Time</label>
                  <div className="flex gap-2">
                    <input
                      type="time"
                      value={dropoffTime}
                      onChange={(e) => setDropoffTime(e.target.value)}
                      className="flex-1 px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                    />
                    <button
                      type="button"
                      onClick={() => handleValidateFlight(true)}
                      disabled={!dropoffAirlineCode || !dropoffFlightNumber || !dropoffDate}
                      className="px-3 py-2 bg-ui-green-strong text-ui-on-primary text-xs rounded-md hover:bg-ui-green-strong/90 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      Validate
                    </button>
                  </div>
                </div>

                <div className="relative md:col-span-2">
                  <label className="block text-xs font-medium text-ui-ink-2 mb-1">Flight Destination (optional)</label>
                  <input
                    type="text"
                    value={dropoffDestinationAirport}
                    onChange={(e) => handleDestinationAirportSearch(e.target.value)}
                    onFocus={() => destinationAirportSearchResults.length > 0 && setShowDestinationAirportDropdown(true)}
                    placeholder="Search airport"
                    className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                  />
                  {showDestinationAirportDropdown && destinationAirportSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-ui-surface border border-ui-line rounded-md shadow-lg max-h-32 overflow-auto">
                      {destinationAirportSearchResults.map((loc, idx) => (
                        <button
                          key={idx}
                          className="w-full px-3 py-2 text-left hover:bg-ui-hover border-b border-ui-divider last:border-0 text-sm"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectDestinationAirport(loc)}
                        >
                          {loc.id && <span className="text-ui-link mr-1">{loc.id}</span>} {loc.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Dropoff Comments */}
            <div className="mt-3">
              <label className="block text-xs font-medium text-ui-ink-2 mb-1">Dropoff Comments (optional)</label>
              <input
                type="text"
                value={dropoffComments}
                onChange={(e) => setDropoffComments(e.target.value)}
                placeholder="Comments for dropoff location..."
                className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
              />
            </div>
          </div>

          {/* Passengers Section */}
          <div className="bg-ui-sunk rounded-lg p-4">
            <h4 className="text-md font-semibold text-ui-ink mb-4">Passengers</h4>

            {/* Added Passengers List */}
            {passengers.length > 0 && (
              <div className="mb-4 space-y-2">
                {passengers.map((p, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-ui-surface p-3 rounded-md border border-ui-line">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-ui-blue-bg rounded-full flex items-center justify-center">
                        <span className="text-ui-link text-sm font-semibold">
                          {p.firstName?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <div className="font-medium text-sm text-ui-ink">
                          {p.firstName} {p.lastName}
                          {p.vip && <span className="ml-2 px-1.5 py-0.5 bg-ui-amber-bg text-ui-amber-fg text-xs rounded">VIP</span>}
                          {p.isExecutive && <span className="ml-2 px-1.5 py-0.5 bg-ui-blue-bg text-ui-link text-xs rounded">Executive</span>}
                        </div>
                        <div className="text-xs text-ui-muted">{p.email} {p.mobile && `• ${p.mobile}`}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => removePassenger(idx)}
                      className="text-ui-red-fg hover:text-ui-red-fg p-1"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Executive Search */}
            <div className="mb-4">
              <label className="block text-xs font-medium text-ui-ink-2 mb-1">Search Executive</label>
              <div className="relative">
                <input
                  type="text"
                  value={executiveSearch}
                  onChange={(e) => handleExecutiveSearch(e.target.value)}
                  onFocus={() => executiveSearchResults.length > 0 && setShowExecutiveDropdown(true)}
                  placeholder="Type to search executives..."
                  className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
                />
                {showExecutiveDropdown && executiveSearchResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-ui-surface border border-ui-line rounded-md shadow-lg max-h-48 overflow-auto">
                    {executiveSearchResults.map((exec) => (
                      <button
                        key={exec.id}
                        className="w-full px-3 py-2 text-left hover:bg-ui-hover border-b border-ui-divider last:border-0"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => addExecutiveAsPassenger(exec)}
                      >
                        <div className="font-medium text-ui-ink text-sm">
                          {exec.full_name || `${exec.first_name} ${exec.last_name}`}
                        </div>
                        <div className="text-xs text-ui-muted">
                          {exec.email && <span>{exec.email}</span>}
                          {exec.phone && <span> • {exec.phone}</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Manual Passenger Entry */}
            {!showManualForm ? (
              <button
                onClick={() => setShowManualForm(true)}
                className="text-sm text-ui-link hover:text-ui-link flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add passenger manually
              </button>
            ) : (
              <div className="border border-ui-line rounded-md p-3 bg-ui-surface">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-medium text-ui-ink-2">Add Manual Passenger</span>
                  <button
                    onClick={() => setShowManualForm(false)}
                    className="text-ui-muted hover:text-ui-ink-3"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-ui-ink-2 mb-1">Title</label>
                    <select
                      value={manualPassenger.title}
                      onChange={(e) => setManualPassenger({ ...manualPassenger, title: e.target.value })}
                      className="w-full px-3 py-2 border border-ui-control rounded-md text-sm"
                    >
                      {TITLES.map((t) => (
                        <option key={t} value={t}>{t || '(none)'}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center gap-2 mt-5">
                      <input
                        type="checkbox"
                        checked={manualPassenger.vip}
                        onChange={(e) => setManualPassenger({ ...manualPassenger, vip: e.target.checked })}
                        className="rounded border-ui-control"
                      />
                      <span className="text-sm font-medium text-ui-ink-2">VIP</span>
                    </label>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ui-ink-2 mb-1">First Name *</label>
                    <input
                      type="text"
                      value={manualPassenger.firstName}
                      onChange={(e) => setManualPassenger({ ...manualPassenger, firstName: e.target.value })}
                      className="w-full px-3 py-2 border border-ui-control rounded-md text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ui-ink-2 mb-1">Last Name *</label>
                    <input
                      type="text"
                      value={manualPassenger.lastName}
                      onChange={(e) => setManualPassenger({ ...manualPassenger, lastName: e.target.value })}
                      className="w-full px-3 py-2 border border-ui-control rounded-md text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ui-ink-2 mb-1">Email</label>
                    <input
                      type="email"
                      value={manualPassenger.email}
                      onChange={(e) => setManualPassenger({ ...manualPassenger, email: e.target.value })}
                      className="w-full px-3 py-2 border border-ui-control rounded-md text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-ui-ink-2 mb-1">Mobile Phone</label>
                    <input
                      type="tel"
                      value={manualPassenger.mobile}
                      onChange={(e) => setManualPassenger({ ...manualPassenger, mobile: e.target.value })}
                      placeholder="+1 555-123-4567"
                      className="w-full px-3 py-2 border border-ui-control rounded-md text-sm"
                    />
                  </div>
                </div>
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={addManualPassenger}
                    disabled={!manualPassenger.firstName || !manualPassenger.lastName}
                    className="px-3 py-1.5 bg-ui-primary text-ui-on-primary text-sm rounded-md hover:bg-ui-primary-hover disabled:opacity-50"
                  >
                    Add Passenger
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Comments Section */}
          <div className="bg-ui-sunk rounded-lg p-4">
            <label className="block text-xs font-medium text-ui-ink-2 mb-1">Special Instructions (optional)</label>
            <textarea
              value={clientComments}
              onChange={(e) => setClientComments(e.target.value)}
              rows={2}
              placeholder="Any special requests for the driver..."
              className="w-full px-3 py-2 border border-ui-control rounded-md focus:outline-none focus:ring-2 focus:ring-ui-primary text-sm"
            />
          </div>

          {/* Booking Information Section - Confirmation Number & Price */}
          <div className="bg-ui-green-bg rounded-lg p-4 border border-ui-green-line">
            <h4 className="text-md font-semibold text-ui-ink mb-4">Booking Information</h4>

            <div className="grid md:grid-cols-2 gap-4">
              {/* Confirmation Number */}
              <div>
                <label className="block text-xs font-medium text-ui-ink-2 mb-1">Confirmation Number</label>
                <div className="flex items-center gap-2">
                  {component.confirmation_number || componentData.urbanride_urcn ? (
                    <div className="px-3 py-2 bg-ui-surface border border-ui-green-line rounded-md text-lg font-bold text-ui-green-fg">
                      {component.confirmation_number || componentData.urbanride_urcn}
                    </div>
                  ) : (
                    <div className="px-3 py-2 bg-ui-surface border border-ui-line rounded-md text-sm text-ui-muted">
                      Not confirmed
                    </div>
                  )}
                </div>
              </div>

              {/* Price/Rate */}
              <div>
                <label className="block text-xs font-medium text-ui-ink-2 mb-1">Price</label>
                <div className="flex items-center gap-2">
                  {componentData.rate || componentData.rate_ext ? (
                    <div className="px-3 py-2 bg-ui-surface border border-ui-green-line rounded-md text-lg font-bold text-ui-link">
                      {componentData.rate || componentData.rate_ext}
                    </div>
                  ) : (
                    <div className="px-3 py-2 bg-ui-surface border border-ui-line rounded-md text-sm text-ui-muted">
                      Not specified
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Vehicle Info if available */}
            {componentData.vehicle_class && (
              <div className="mt-4 flex items-center gap-3">
                {componentData.vehicle_image_url && (
                  <img
                    src={componentData.vehicle_image_url}
                    alt={componentData.vehicle_class}
                    className="w-16 h-10 object-contain"
                  />
                )}
                <div>
                  <div className="text-sm font-medium text-ui-ink">{componentData.vehicle_class}</div>
                  {componentData.capacity && (
                    <div className="text-xs text-ui-muted">
                      Capacity: {componentData.capacity} {componentData.luggage && `• Luggage: ${componentData.luggage}`}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Booking Mode indicator */}
            {componentData.booking_mode && (
              <div className="mt-3">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                  componentData.booking_mode === 'production' ? 'bg-ui-green-bg text-ui-green-fg' : 'bg-ui-amber-bg text-ui-amber-fg'
                }`}>
                  <span className={`w-2 h-2 rounded-full ${componentData.booking_mode === 'production' ? 'bg-ui-green-strong' : 'bg-ui-amber-strong'}`}></span>
                  {componentData.booking_mode === 'production' ? 'Production Booking' : 'Test Booking'}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between pt-4 mt-4 border-t border-ui-line">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-ui-control rounded-md hover:bg-ui-hover text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isUpdating}
            className="px-6 py-2 bg-ui-primary text-ui-on-primary rounded-md hover:bg-ui-primary-hover disabled:opacity-50 text-sm"
          >
            {isUpdating ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditChauffeuredServiceModal;
